import { Box, Flex, Grid, HStack, Select, Text } from '@gazin/ecommerce-ui'
import { useRouter } from 'next/router'
import { PaginationComponent } from 'src/components/Pagination'
import { ProductVerticalWithAnalytics } from 'src/components/productVertical'
import {
  Product,
  Suggestion,
} from 'src/services/linx-api/dtos/search-products-response'
import { calculateProductsForPage } from '../../utils'
import { useProductShowcases } from 'src/contexto/hooks/useProductShowcases'
import { LinxShowcaseType } from 'src/contexto/types'
import { mapProductToAnalyticsProductLinx } from 'src/components/productVertical/utils'
import { ProductsSortingKey } from 'src/util/enums/products-sorting-key'
import dynamic from 'next/dynamic'

const ProductSectionSearch = dynamic(
  () =>
    import('src/components/ProductSectionSearch').then(
      (module) => module.ProductSectionSearch
    ),
  { ssr: false }
)

export type ProductListDesktopProps = {
  alterarOrdenacao: (value: string) => void
  configPaginas: {
    currentPage: number
    firstPage: number
    lastPage: number
    nextPage: number
    prevPage: number
  }
  isCategoryPage?: boolean
  mapLinxProductToProductVertical: (product: Product, isMobile?: boolean) => any
  pagina: number
  parametros: { ordenacao: string }
  produtos?: Product[]
  resultsSize?: number
  suggestedSearches?: Suggestion[]
}

export const ProductListDesktop = ({
  alterarOrdenacao,
  configPaginas,
  isCategoryPage,
  mapLinxProductToProductVertical,
  pagina,
  parametros,
  produtos,
  resultsSize,
  suggestedSearches,
}: ProductListDesktopProps) => {
  const route = useRouter()

  const { showcaseTop, showcaseMiddle, showcaseBottom } = useProductShowcases(
    isCategoryPage ? LinxShowcaseType.CATEGORY : LinxShowcaseType.SEARCH,
    isCategoryPage
      ? route.query && route.query.categoria && route.query.categoria[0]
      : undefined
  )

  return (
    <Box width="100%" maxWidth="60rem">
      <Flex flexDir="column">
        {showcaseTop.length > 0 &&
          showcaseTop?.map((showcase) => (
            <ProductSectionSearch
              key={showcase.id}
              title={showcase.title}
              subtitle={showcase.subtitle}
              products={showcase.displays[0]?.recommendations}
            />
          ))}
      </Flex>

      {route.query.busca && (
        <HStack spacing={2}>
          <Text fontSize="1.5rem" fontWeight={600} color="#363843">
            Você pesquisou por ‘
            <Text fontSize="1.5rem" fontWeight={600} color="#0D71F0" as="span">
              {`${route.query.busca?.[0]}`}
            </Text>
            ’
          </Text>
        </HStack>
      )}
      <HStack width="100%" justifyContent="space-between" alignItems="center">
        <Text fontSize="0.875rem" color="#7C8096">
          {resultsSize && calculateProductsForPage(pagina, 18, resultsSize)}
        </Text>

        <Flex
          padding="0.75rem 0rem 2rem 0.375rem"
          justifyContent="flex-end"
          display={{ md: 'none', lg: 'flex' }}
        >
          <Flex alignItems="center">
            <Text
              as="span"
              color="#646981"
              fontSize="0.875rem"
              marginRight="0.875rem"
              width="50%"
            >
              Ordenar por:
            </Text>
            <Select
              borderRadius=".25rem"
              variant="outline"
              onChange={(e) => alterarOrdenacao(e.target.value)}
              defaultValue={parametros.ordenacao}
            >
              <option value={ProductsSortingKey.Relevance}>Populares</option>
              <option value={ProductsSortingKey.AscPrice}>Menor preço</option>
              <option value={ProductsSortingKey.DescPrice}>Maior preço</option>
              <option value={ProductsSortingKey.DescDiscount}>
                Maiores descontos
              </option>
            </Select>
          </Flex>
        </Flex>
      </HStack>

      <Grid
        gap={{ base: '1.25rem 0.1875rem', lg: '2rem' }}
        gridTemplateColumns={{
          base: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        }}
        overflow="hidden"
        width="100%"
        paddingBottom={'16px'}
      >
        {produtos?.map((product, index) => (
          <ProductVerticalWithAnalytics
            key={index}
            product={mapLinxProductToProductVertical(product, false)}
            analyticsProduct={mapProductToAnalyticsProductLinx(product)}
          />
        ))}
      </Grid>
      <Flex justifyContent="center" width="100%">
        <PaginationComponent pagination={configPaginas} />
      </Flex>
      <Flex flexDir="column" mt={8}>
        {showcaseMiddle.length > 0 &&
          showcaseMiddle?.map((showcase) => (
            <ProductSectionSearch
              key={showcase.id}
              title={showcase.name}
              subtitle={showcase.title}
              products={showcase.displays[0]?.recommendations}
            />
          ))}

        {showcaseBottom.length > 0 &&
          showcaseBottom?.map((showcase) => (
            <ProductSectionSearch
              key={showcase.id}
              title={showcase.title}
              subtitle={showcase.subtitle}
              products={showcase.displays[0]?.recommendations}
            />
          ))}
      </Flex>

      {!!suggestedSearches?.length && (
        <Box marginTop="3rem">
          <Text color="#646981" fontWeight={600} as="span" fontSize="1rem">
            Pesquisas rápidas
          </Text>
          <Flex justifyContent="flex-start" width="100%" flexWrap="wrap">
            {suggestedSearches.map((quer, index) => (
              <Box
                key={index}
                border="1px solid #7C8096"
                paddingX="12px"
                paddingY="8px"
                borderRadius="4px"
                marginRight="16px"
                marginTop="16px"
                color="#7C8096"
                fontSize="14px"
                height="40px"
              >
                <Text
                  as="span"
                  color="#7C8096"
                  cursor="pointer"
                  onClick={() => {
                    route.push({
                      pathname: '/busca/[...busca]',
                      query: { busca: quer.query },
                    })
                  }}
                >
                  {quer.query}
                </Text>
              </Box>
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  )
}
